import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { createAction } from '@reduxjs/toolkit';
import {
  COPY_REFERENCE_DISPLAYED_ACTION_TYPE,
  CopyReferenceDisplayed,
} from 'components/CopyReferenceFeedback/CopyReferenceFeedback.types';
import { openCopyReferenceFeedback } from './CopyReferenceWithFeedback';

interface CopySelectionWrapperProps {
  referenceNumber: string;
  className?: string;
  children: React.ReactNode;
}

const copyReferenceDisplayed = createAction<CopyReferenceDisplayed>(COPY_REFERENCE_DISPLAYED_ACTION_TYPE);

const CopySelectionWrapper = ({ referenceNumber, className, children }: CopySelectionWrapperProps) => {
  const dispatch = useDispatch();
  const spanRef = useRef<HTMLSpanElement | null>(null);
  const hadSelection = useRef(false);

  useEffect(() => {
    const findTargetId = (startEl: Element | null): string | undefined => {
      let el: Element | null = startEl;
      for (let i = 0; i < 4 && el; i++) {
        const found = el.querySelector<HTMLElement>('[data-copy-feedback-id]');
        if (found) return found.dataset.copyFeedbackId;
        el = el.parentElement;
      }
      return undefined;
    };

    const handleMouseUp = (e: MouseEvent) => {
      // Ignore right-click and middle-click — only left-click drag should trigger selection tracking
      if (e.button !== 0) return;
      const selection = globalThis.getSelection();
      if (!selection) return;

      const text = selection.toString().trim();
      if (text.length < 4) return;

      if (!spanRef.current || !selection.rangeCount) return;

      const range = selection.getRangeAt(0);
      if (!range.intersectsNode(spanRef.current)) return;

      hadSelection.current = true;
      const targetId = findTargetId(spanRef.current.parentElement);
      openCopyReferenceFeedback(referenceNumber, 'SELECTION', targetId);
    };

    dispatch(copyReferenceDisplayed({ referenceNumber }));
    document.addEventListener('mouseup', handleMouseUp);
    return () => document.removeEventListener('mouseup', handleMouseUp);
  }, [dispatch, referenceNumber]);

  const handleClickCapture = useCallback((e: React.MouseEvent) => {
    if (hadSelection.current) {
      hadSelection.current = false;
      e.preventDefault();
      e.stopPropagation();
    }
  }, []);

  return (
    <span ref={spanRef} className={className} onClickCapture={handleClickCapture}>
      {children}
    </span>
  );
};

export default CopySelectionWrapper;
export { CopySelectionWrapper };
